import { useEffect, useRef, useState, type RefObject } from 'react';
import { t } from '../../i18n/idioma';
import { useIdioma } from '../../i18n/useIdioma';
import { useFechar } from '../../input/useAtalhos';
import type { ModoTela } from './useModoTela';

export type PassoTutorial = 'viajar' | 'lojas' | 'loja' | 'inventario' | 'barra' | 'cacar';

export type FaltaBarra = { cura: boolean; mana: boolean; ataque: boolean; f1: boolean; f2: boolean };

const PASSOS: ReadonlyArray<{ id: PassoTutorial; alvo: string }> = [
  { id: 'viajar', alvo: '.win--viajar' },
  { id: 'lojas', alvo: '.pop[data-pop="lojas"]' },
  { id: 'loja', alvo: '.mt-loja' },
  { id: 'inventario', alvo: '.win--inventory' },
  { id: 'barra', alvo: '.win--combate' },
  { id: 'cacar', alvo: '.win--hunt' }
];

export function DicasHud({
  mundoRef, modo, ativo, inventarioRestam, barraFalta, ataqueNome, onPasso, onConcluir
}: {
  mundoRef: RefObject<HTMLElement | null>;
  modo: ModoTela;
  ativo: boolean;
  inventarioRestam: number;
  barraFalta: FaltaBarra;
  ataqueNome: string;
  onPasso(passo: PassoTutorial): void;
  onConcluir(): void;
}) {
  useIdioma();
  const [passo, setPasso] = useState(() => (ativo ? 0 : -1));
  const dicaRef = useRef<HTMLDivElement>(null);
  const [caixa, setCaixa] = useState<{
    passo: PassoTutorial;
    x: number; y: number; w: number; h: number; bx: number; seta: number; acima: boolean; topo: number | null;
  } | null>(null);

  useEffect(() => { setPasso(ativo ? 0 : -1); }, [ativo]);

  const aberta = ativo && passo >= 0 && passo < PASSOS.length;
  const atual = aberta ? PASSOS[passo] : null;
  useEffect(() => { if (atual) onPasso(atual.id); }, [atual, onPasso]);
  useFechar(() => {}, { ativo: aberta, modal: true });

  useEffect(() => {
    if (!aberta || !atual) { setCaixa(null); return; }
    const medir = () => {
      const mundo = mundoRef.current;
      if (!mundo) return;
      const alvo = [...mundo.querySelectorAll(atual.alvo)].find(e => (e as HTMLElement).offsetParent !== null) as HTMLElement | undefined;
      const r = alvo?.getBoundingClientRect();
      if (!alvo || !r || r.width < 8 || r.height < 8) {
        setCaixa(prev => (prev === null ? prev : null));
        return;
      }
      const w0 = mundo.getBoundingClientRect();
      const x = r.left - w0.left;
      const y = r.top - w0.top;
      const w = r.width;
      const h = r.height;
      const W = w0.width;
      const H = w0.height;
      const margem = 8;
      const largura = 260;
      const altura = dicaRef.current?.offsetHeight ?? 190;
      const abaixo = y + h + 12;
      const cabeAcima = y - 12 >= altura + margem;
      const cabeAbaixo = abaixo + altura <= H - margem;
      const espacoEsq = x - 12;
      const espacoDir = W - (x + w) - 12;
      const cabeLado = !cabeAcima && !cabeAbaixo && (espacoEsq >= largura || espacoDir >= largura);
      let acima = false;
      let topo: number | null = null;
      let bx = Math.max(margem, Math.min(x + w / 2 - largura / 2, W - largura - margem));
      if (cabeLado) {
        bx = espacoEsq >= largura ? x - largura - 12 : x + w + 12;
        bx = Math.max(margem, Math.min(bx, W - largura - margem));
        topo = Math.max(margem, Math.min(y + 24, H - margem - altura));
      } else if (cabeAcima && (!cabeAbaixo || y > H / 2)) {
        acima = true;
      } else if (cabeAbaixo) {
        topo = abaixo;
      } else {
        topo = margem;
      }
      const seta = Math.max(10, Math.min(x - bx + w / 2 - 7, largura - 28));
      const passo = atual.id;
      setCaixa(prev => (
        prev && prev.passo === passo && prev.x === x && prev.y === y && prev.w === w && prev.h === h
          && prev.bx === bx && prev.seta === seta && prev.acima === acima && prev.topo === topo
          ? prev
          : { passo, x, y, w, h, bx, seta, acima, topo }
      ));
    };
    medir();
    let quadro = 0;
    const seguir = () => {
      medir();
      quadro = requestAnimationFrame(seguir);
    };
    quadro = requestAnimationFrame(seguir);
    window.addEventListener('resize', medir);
    return () => {
      cancelAnimationFrame(quadro);
      window.removeEventListener('resize', medir);
    };
  }, [aberta, atual, modo, mundoRef]);

  if (!aberta || !atual) return null;
  const noLugar = caixa !== null && caixa.passo === atual.id ? caixa : null;
  const n = passo + 1;
  const inventario = atual.id === 'inventario';
  const barra = atual.id === 'barra';
  const faltaInventario = inventario && inventarioRestam > 0;
  const itensBarra = barra
    ? [
        barraFalta.ataque ? t('hud.dica.barra.ataque', { ataque: ataqueNome }) : ''
      ].filter(Boolean)
    : [];
  const faltaBarra = itensBarra.length > 0;
  const falta = faltaInventario || faltaBarra;
  const proxima = () => {
    if (falta) return;
    if (passo >= PASSOS.length - 1) { setPasso(-1); onConcluir(); return; }
    setPasso(passo + 1);
  };
  return (
    <>
      {atual.id !== 'inventario' && <div className="tutorial-trava" />}
      {noLugar && (
        <div className="realce" style={{ left: noLugar.x - 4, top: noLugar.y - 4, width: noLugar.w + 8, height: noLugar.h + 8 }} />
      )}
      {noLugar && (
      <div
        ref={dicaRef}
        className={`pdica${noLugar.acima ? ' baixo' : ''}`}
        role="dialog"
        aria-label={t('hud.dica.aria')}
        style={{
          left: noLugar.bx,
          ...(noLugar.acima
            ? { top: 'auto', bottom: `calc(100% - ${Math.max(12, noLugar.y - 12)}px)` }
            : { top: noLugar.topo ?? 8, bottom: 'auto' }),
          ['--seta' as string]: `${noLugar.seta}px`
        }}
      >
        <b>{t(`hud.dica.${n}.t`)}</b>
        {t(`hud.dica.${n}.d`, barra ? { ataque: ataqueNome } : undefined)}
        {faltaInventario && <span className="pdica__falta">{t('hud.dica.falta', { n: inventarioRestam })}</span>}
        {faltaBarra && <span className="pdica__falta">{t('hud.dica.barraFalta', { itens: itensBarra.join(', ') })}</span>}
        <div className="p-row">
          <button type="button" className="btn btn--primary btn--tiny" style={{ marginLeft: 'auto' }} disabled={falta} onClick={proxima}>
            {t(n < PASSOS.length ? 'hud.dica.proxima' : 'hud.dica.entendi')}
          </button>
        </div>
      </div>
      )}
    </>
  );
}
